import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { getCartItemsFromLocalStorageAction } from "../../../redux/slices/cart/cartSlice";
import { placeOrderAction } from "../../../redux/slices/orders/ordersSlices";
import { getUserProfileAction } from "../../../redux/slices/users/usersSlice";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import Skeleton from "../../Skeleton/Skeleton";
import AddShippingAddress from "../Forms/AddShippingAddress";
import { getOptimizedImage } from '../../../utils/cloudinary';

export default function OrderPayment() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { sumTotalPrice } = location.state || {};
  
  // State for edit mode
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressUpdated, setAddressUpdated] = useState(false);
  
  // Get data from store
  const { cartItems } = useSelector((state) => state?.carts);
  const { profile, loading: profileLoading, error: profileError } = useSelector((state) => state?.users);
  const { loading: orderLoading, error: orderErr, success } = useSelector((state) => state?.orders);
  
  const user = profile;
  const shippingAddress = user?.shippingAddress;
  const hasShippingAddress = shippingAddress && Object.keys(shippingAddress).length > 0;
  
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Load data once
  useEffect(() => {
    dispatch(getCartItemsFromLocalStorageAction());
    dispatch(getUserProfileAction());
  }, [dispatch]);
  
  // Check when data is actually available
  useEffect(() => {
    if (cartItems !== undefined && user !== undefined) {
      setIsDataLoaded(true);
    }
  }, [cartItems, user]);
  
  // Handle successful order placement
  useEffect(() => {
    if (success) {
      Swal.fire({
        icon: "success",
        title: t('orderPayment.orderPlaced'),
        text: t('orderPayment.orderPlacedMessage'),
        confirmButtonText: t('orderPayment.viewOrders'),
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/profile");
        }
      });
    }
  }, [success, navigate, t]);
  
  // Refresh profile after address update
  useEffect(() => {
    if (addressUpdated) {
      dispatch(getUserProfileAction());
      setAddressUpdated(false);
      setIsEditingAddress(false);
    }
  }, [addressUpdated, dispatch]);
  
  // Calculate totals
  const subtotal = cartItems?.reduce((acc, item) => acc + (item?.totalPrice || 0), 0) || 0;
  const totalToPay = sumTotalPrice || subtotal;
  
  // Show loading until we have actual data
  if (!isDataLoaded || profileLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
         <div className="bg-white">
    <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
      <Skeleton className="h-8 w-64 mb-8" /> {/* page title */}
      
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
        {/* Contact & Payment form skeleton (left column on desktop) */}
        <div className="space-y-8">
          {/* Contact section */}
          <div>
            <Skeleton className="h-6 w-40 mb-4" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          
          {/* Payment section */}
          <div>
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
          
          {/* Shipping address section */}
          <div>
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="space-y-3">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-10 rounded-md" />
                <Skeleton className="h-10 rounded-md" />
              </div>
            </div>
          </div>
          
          {/* Place order button */}
          <Skeleton className="h-12 w-full rounded-md" />
        </div>

        {/* Order summary skeleton (right column on desktop) */}
        <div className="mt-10 lg:mt-0">
          <Skeleton className="h-6 w-32 mb-6" />
          
          <div className="flow-root">
            <ul className="-my-6 divide-y divide-gray-200">
              {Array.from({ length: 2 }).map((_, i) => (
                <li key={i} className="flex py-6 space-x-4">
                  <Skeleton className="h-24 w-24 rounded-md flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-5 w-12" />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Subtotal / shipping / total */}
          <div className="border-t border-gray-200 mt-6 pt-6 space-y-3">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
      </div>
    );
  }
  
  // Show error
  if (profileError) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ErrorMsg message={profileError?.message || t('orderPayment.errorLoadingProfile')} />
      </div>
    );
  }
  
  // Check empty cart
  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <p className="text-gray-600 mb-4">{t('orderPayment.emptyCart')}</p>
        <button
          onClick={() => navigate("/products")}
          className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
        >
          {t('orderPayment.continueShopping')}
        </button>
      </div>
    );
  }
  
  const placeOrderHandler = () => {
    if (!hasShippingAddress) {
      Swal.fire({
        icon: "warning",
        title: t('orderPayment.missingAddress'),
        text: t('orderPayment.addAddressMessage'),
        confirmButtonText: t('orderPayment.addAddress'),
      });
      setIsEditingAddress(true);
      return;
    }
    
    Swal.fire({
      title: t('orderPayment.confirmOrder'),
      text: t('orderPayment.confirmOrderMessage', { total: totalToPay }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t('orderPayment.confirm'),
      cancelButtonText: t('orderPayment.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          placeOrderAction({
            shippingAddress,
            orderItems: cartItems,
            totalPrice: totalToPay,
          })
        );
      }
    });
  };
  
  const handleEditAddress = () => {
    setIsEditingAddress(true);
  };
  
  const handleAddressUpdated = () => {
    setAddressUpdated(true);
  };
  
  const handleCancelEdit = () => {
    setIsEditingAddress(false);
  };

  const formattedTotal = totalToPay.toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}+ 'MXN');

  return (
    <>
      {orderErr && <ErrorMsg message={orderErr?.message} />}
      <div className="bg-gray-50 min-h-screen">
        <main className="mx-auto max-w-7xl px-4 pt-16 pb-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:max-w-none">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-8">
              {t('orderPayment.checkout')}
            </h1>

            <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
              {/* Left Column - Shipping Address */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium text-gray-900">
                    {t('orderPayment.shippingInformation')}
                  </h2>
                  {hasShippingAddress && !isEditingAddress && (
                    <button
                      onClick={handleEditAddress}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      {t('orderPayment.editAddress')}
                    </button>
                  )}
                </div>
                
                {!isEditingAddress ? (
                  // Display existing shipping address
                  hasShippingAddress ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <p className="text-xs text-gray-500">{t('orderPayment.fullName')}</p>
                            <p className="text-sm text-gray-900 font-medium">
                              {shippingAddress.nombre} {shippingAddress.paterno}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('orderPayment.phone')}</p>
                            <p className="text-sm text-gray-900">{shippingAddress.telefono}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('orderPayment.address')}</p>
                            <p className="text-sm text-gray-900">{shippingAddress.direccion}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('orderPayment.neighborhood')}</p>
                            <p className="text-sm text-gray-900">{shippingAddress.colonia}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-gray-500">{t('orderPayment.municipality')}</p>
                              <p className="text-sm text-gray-900">{shippingAddress.delegacion}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('orderPayment.postalCode')}</p>
                              <p className="text-sm text-gray-900">{shippingAddress.zipcode}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('orderPayment.state')}</p>
                            <p className="text-sm text-gray-900">{shippingAddress.estado}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800 flex items-center">
                          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {t('orderPayment.addressConfirmed')}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-500">{t('orderPayment.noAddressFound')}</p>
                      <button
                        onClick={() => setIsEditingAddress(true)}
                        className="mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium"
                      >
                        {t('orderPayment.addAddress')}
                      </button>
                    </div>
                  )
                ) : (
                  // Show form to add/edit address
                  <div>
                    <AddShippingAddress 
                      onSuccess={handleAddressUpdated} 
                      onCancel={handleCancelEdit}
                      initialData={shippingAddress}
                    />
                  </div>
                )}
              </div>

              {/* Right Column - Order Summary */}
              <div className="mt-10 lg:mt-0">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {t('orderPayment.orderSummary')}
                </h2>

                <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                  <ul role="list" className="divide-y divide-gray-200">
                    {cartItems.map((product, index) => (
                      <li key={product._id || product.product || index} className="flex py-6 px-4 sm:px-6">
                        <div className="flex-shrink-0">
                          <img
                            src={getOptimizedImage(product.image, { width: 80, height: 80, quality: 'auto', format: 'auto' })}
                            alt={product.name}
                            className="w-20 h-20 rounded-md object-cover"
                          />
                        </div>
                        <div className="ml-6 flex flex-1 flex-col">
                          <div className="flex">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {product.name}
                              </h3>
                              <p className="mt-1 text-sm text-gray-500">
                                {t('orderPayment.size')}: {product.size || t('orderPayment.default')} | {t('orderPayment.style')}: {product.style || t('orderPayment.standard')}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-1 items-end justify-between pt-2">
                            <p className="text-sm font-medium text-gray-900">
                              {`$${product?.price.toLocaleString('en-US')}.00 x ${product?.qty} = $ ${product?.totalPrice.toLocaleString('en-US')}.00`}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  
                  <dl className="space-y-6 border-t border-gray-200 py-6 px-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <dt className="text-sm text-gray-600">{t('orderPayment.subtotal')}</dt>
                      <dd className="text-sm font-medium text-gray-900">{`$${subtotal.toLocaleString('en-US')}.00`}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-sm text-gray-600">{t('orderPayment.shipping')}</dt>
                      <dd className="text-sm font-medium text-gray-900">{t('orderPayment.free')}</dd>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                      <dt className="text-base font-medium text-gray-900">{t('orderPayment.total')}</dt>
                      <dd className="text-base font-medium text-orange-600">{`$${totalToPay.toLocaleString('en-US')}.00 MXN`}</dd>
                    </div>
                  </dl>

                  <div className="border-t border-gray-200 py-6 px-4 sm:px-6">
                    {orderLoading ? (
                       <div className="bg-white">
    <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
      <Skeleton className="h-8 w-64 mb-8" /> {/* page title */}
      
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
        {/* Contact & Payment form skeleton (left column on desktop) */}
        <div className="space-y-8">
          {/* Contact section */}
          <div>
            <Skeleton className="h-6 w-40 mb-4" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          
          {/* Payment section */}
          <div>
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
          
          {/* Shipping address section */}
          <div>
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="space-y-3">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-10 rounded-md" />
                <Skeleton className="h-10 rounded-md" />
              </div>
            </div>
          </div>
          
          {/* Place order button */}
          <Skeleton className="h-12 w-full rounded-md" />
        </div>

        {/* Order summary skeleton (right column on desktop) */}
        <div className="mt-10 lg:mt-0">
          <Skeleton className="h-6 w-32 mb-6" />
          
          <div className="flow-root">
            <ul className="-my-6 divide-y divide-gray-200">
              {Array.from({ length: 2 }).map((_, i) => (
                <li key={i} className="flex py-6 space-x-4">
                  <Skeleton className="h-24 w-24 rounded-md flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-5 w-12" />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Subtotal / shipping / total */}
          <div className="border-t border-gray-200 mt-6 pt-6 space-y-3">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
                    ) : (
                      <button
                        onClick={placeOrderHandler}
                        className={`w-full rounded-md border border-transparent py-3 px-4 text-base font-medium text-white shadow-sm ${
                          !hasShippingAddress && !isEditingAddress
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                        }`}
                      >
                        {!hasShippingAddress && !isEditingAddress
                          ? t('orderPayment.addAddressToContinue')
                          : t('orderPayment.confirmPayment', { total: formattedTotal })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}